// src/services/dstackService.ts
import apiClient from './api';
import axios from 'axios'; // <-- Keep axios import for isAxiosError check

interface DstackServiceOutput {
  reasoning: string | null;
  answer: string;
}

class DstackService {

  /**
   * Parses LLM output text containing <think>...</think> tags.
   *
   * Splits the input string into reasoning (text within the tags)
   * and answer (text after the closing tag).
   *
   * @param {string} inputText - The raw text output from the LLM.
   *                             Expected format: "<think>reasoning</think>answer"
   * @returns {DstackServiceOutput}
   *          An object with 'reasoning' and 'answer' properties.
   *          'reasoning' will be null if the <think> tags are not found.
   *          Whitespace is trimmed from both parts.
   */
  parseLlmOutputWithThinking(inputText: string) : DstackServiceOutput {
    if (!inputText) {
      return { reasoning: null, answer: '' };
    }

    // Regex to capture:
    // 1. (.*?): Content inside <think>...</think> (non-greedy) -> reasoning
    // 2. (.*): Content after </think> -> answer
    // 's' flag allows '.' to match newline characters.
    const regex = /<think>(.*?)<\/think>(.*)/s;
    const match = inputText.match(regex);

    if (match && match.length === 3) {
      // match[1] contains the reasoning text
      // match[2] contains the answer text
      const reasoning = match[1].trim();
      const answer = match[2].trim();
      return { reasoning, answer };
    } else {
      // If the pattern doesn't match, assume the entire input is the answer.
      console.warn("Input text did not contain <think>...</think> tags. Treating entire input as answer.");
      return { reasoning: null, answer: inputText.trim() };
    }
  }


  /**
   * Fetches dstack for a given call stack and optional code snippet from the backend API.
   *
   * @param {string} callStack - The call stack string.
   * @param {string} [codeSnippet] - An optional code snippet string.
   * @returns {Promise<DstackServiceOutput>} A promise that resolves to the markdown string containing the dstack.
   */
  async getDstack(callStack: string, codeSnippet?: string): Promise<DstackServiceOutput> {
    console.log("getDstack(): Calling backend API for dstack.", { callStack, codeSnippet });
    
    try {
      const response = await apiClient.post('/api/dstack', {
        callStack,
        codeSnippet, // Send codeSnippet only if it has value
      });

      // Assuming the backend returns the markdown dstack in response.data.dstack
      if (response.data && typeof response.data.dstack === 'string') {
        console.log("getDstack(): Got dstack from backend API.", response.data.dstack);
        // Parse the raw string before returning
        const parsedOutput = this.parseLlmOutputWithThinking(response.data.dstack);
        console.log("getDstack(): Parsed dstack:", parsedOutput);
        return parsedOutput;
      } else {
        throw new Error('Invalid response format from dstack API.');
      }
    } catch (error) {

      console.error('Error fetching stack dstack:', error);
      // Provide a more user-friendly error message or re-throw
      if (axios.isAxiosError(error) && error.response) {
        // Handle specific API errors if needed
        throw new Error(`API Error: ${error.response.status} - ${error.response.data?.message || error.message}`);
      }
      throw new Error('Failed to fetch stack dstack. Please check the connection or try again.');
    }
  }

}

// Create a singleton instance of the service
const dstackService = new DstackService();

export default dstackService;
